import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const SECTION_ID = 'cart-drawer-section';

/**
 * Añade la variante seleccionada al carrito usando el mismo mecanismo AJAX
 * que cart-upsell.js (CartLinesUpdateEvent + morphSection).
 *
 * @param {HTMLButtonElement} button
 */
async function addCdrProduct(button) {
  const product = button.closest('[data-cdr-product]');
  if (!product) return;

  const select = /** @type {HTMLSelectElement | null} */ (
    product.querySelector('[data-cdr-variant-select]')
  );

  const variantId = select
    ? parseInt(select.value, 10)
    : parseInt(button.dataset.variantId ?? '0', 10);

  if (!variantId) return;

  button.disabled = true;
  const originalText = button.textContent.trim();
  button.textContent = '···';

  const deferredPromise = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      promise: deferredPromise.promise,
    })
  );

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description ?? 'Error al añadir el producto');
    }

    const sectionHtml = data.sections?.[SECTION_ID];
    if (sectionHtml) {
      await morphSection(SECTION_ID, sectionHtml, { mode: 'hydration' });
      requestAnimationFrame(() => {
        const scrollArea = document.querySelector('.cart-drawer__content');
        if (scrollArea) scrollArea.scrollTop = 0;
      });
    }

    deferredPromise.resolve({
      cart: data,
      sections: data.sections ?? {},
    });
  } catch (error) {
    console.error('[Cart Recomendados]', error);
    deferredPromise.reject(error);
    button.disabled = false;
    button.textContent = originalText;
  }
}

// ── Puntitos de paginación ────────────────────────────────────────────────

/**
 * Devuelve el índice del slide activo basándose en la posición visual.
 * Usa getBoundingClientRect para evitar problemas con offsetParent.
 * @param {Element} list
 * @returns {number}
 */
function getActiveIndex(list) {
  const items = list.querySelectorAll('.cdr-product, .cdc-product');
  if (!items.length) return 0;
  const listLeft = list.getBoundingClientRect().left;
  let closest = 0;
  let minDist = Infinity;
  items.forEach((item, i) => {
    const dist = Math.abs(item.getBoundingClientRect().left - listLeft);
    if (dist < minDist) {
      minDist = dist;
      closest = i;
    }
  });
  return closest;
}

/**
 * Actualiza la clase activa de los puntitos de una sección.
 * @param {Element} list
 */
function updateDots(list) {
  const section = list.closest('.cdr-section, .cdc-section');
  if (!section) return;
  const dots = section.querySelectorAll('.cdr-dot, .cdc-dot');
  if (!dots.length) return;
  const activeIndex = getActiveIndex(list);
  const isComplementos = section.classList.contains('cdc-section');
  dots.forEach((dot, i) => {
    dot.classList.toggle(isComplementos ? 'cdc-dot--active' : 'cdr-dot--active', i === activeIndex);
  });
}

/**
 * Desplaza el carrusel hasta el slide indicado.
 * @param {Element} list
 * @param {number} index
 */
function scrollToSlide(list, index) {
  const items = list.querySelectorAll('.cdr-product, .cdc-product');
  const target = items[index];
  if (!target) return;
  const listRect = list.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const scrollOffset = list.scrollLeft + (targetRect.left - listRect.left);
  list.scrollTo({ left: scrollOffset, behavior: 'smooth' });
}

// Event delegation — funciona antes y después de re-renders del carrito.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  // Botón añadir al carrito
  const btn = event.target.closest('[data-cdr-add-btn]');
  if (btn) {
    event.preventDefault();
    addCdrProduct(/** @type {HTMLButtonElement} */ (btn));
    return;
  }

  // Puntito de paginación (cdr-dot y cdc-dot)
  const dot = event.target.closest('.cdr-dot, .cdc-dot');
  if (dot) {
    const section = dot.closest('.cdr-section, .cdc-section');
    if (!section) return;
    const list = section.querySelector('.cdr-list, .cdc-list');
    if (!list) return;
    const index = parseInt(/** @type {HTMLElement} */ (dot).dataset.index ?? '0', 10);
    scrollToSlide(list, index);
  }
});

// Scroll en el carrusel → actualiza puntitos (captura necesaria, scroll no burbuja)
document.addEventListener(
  'scroll',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.matches('.cdr-list, .cdc-list')) return;
    updateDots(event.target);
  },
  { capture: true, passive: true }
);
